import { FC, useEffect, useMemo, useRef, useState } from 'react'
import {
  ArrowLeftIcon,
  DocumentArrowDownIcon,
} from '@heroicons/react/24/outline'
import { v4 as uuidv4 } from 'uuid'
import { importShipmentsFromXLSX } from '../../utils/imports'
import {
  FormFile,
  FormText,
  MainPage,
  PageTitle,
  PrimaryButton,
  SecondaryButton,
  LinkTextIcon,
  ModalLoading,
  ConfirmModalStatus,
  Modal,
} from '../../components'
import { ImportInterface } from '../../interfaces/Import/importInterfase'
import {
  ShipmentGroup,
  ShipmentPieceRow,
} from '../../interfaces/Import/ShipmentGroupInterface'
import {
  BusinessUnitInterface,
  DeliveryEnum,
  PaymentModeId,
} from '../../interfaces'
import { useAppSelector, useAppDispatch } from '../../store/hooks'
import { useAllowedServices } from '../../hooks/useAllowedServices'
import { hydrateBulkCatalogs } from '../../store/slices/bulkCatalogs'
import { setOriginBusinessUnits } from '../../store/slices'
import {
  alertService,
  getFavoriteAndRecentDestinationsDjango,
  getOriginBusinessUnits,
} from '../../services'
import { getShipmentsExisting } from '../../services/ShipmentBulkUploadServices'
import { exportFormatoShipmentXLSX_V2WithCoords } from '../../utils/exports'
import {
  normalizePhone,
  parseFavoriteIdentifier,
  cleanStoreCode,
  extractCoord,
  missingSetupMessage,
} from '../../utils/shipmentImportUtils'
import {
  authorizedServiceOptions,
  authorizingAccountsForMode,
  resolveServiceIdFromCell,
  serviceLabel,
} from '../../utils/authorizedServices'
import {
  adaptLegacyBulkTemplate,
  isLegacyBulkTemplate,
} from '../../utils/legacyBulkTemplate'
import { BULK_TEMPLATE_REQUIRED_HEADERS } from '../../utils/bulkTemplateHeaders'
import { useContractDestinationBUs } from '../../hooks/useContractDestinationBUs'
import {
  augmentCatalogWithContractStores,
  resolveDestinationBusinessUnits,
} from '../../utils/destinationBusinessUnits'
import { DestinationInterface } from '../../interfaces/Account/DestinationInterface'
import ShipmentBulkUploadReview from './ShipmentBulkUploadReview'

interface NormalizationContext {
  typeShipment: { value: string; name: string }[]
  citiesType: any[]
  citiesByBuType: any[]
  businessUnits: BusinessUnitInterface[]
  identificationTypes: any[]
  pieceCategories: any[]
  user: any
}

const normalizeShipmentByVersion = (
  shipment: ImportInterface,
  version: 'V1' | 'V2',
  context: NormalizationContext
) => {
  const {
    typeShipment,
    citiesType,
    citiesByBuType,
    businessUnits,
    identificationTypes,
    pieceCategories,
    user,
  } = context

  shipment.A = shipment.A?.toString()
  shipment.B = shipment.B?.toString()

  // G.4a fix: trim Excel cell value before matching. User report: cells with
  // trailing whitespace from copy-paste fell through and defaulted to '' → UI
  // rendered as if OFFICE because deliveryType empty. Trim + normalize case.
  const rawDeliveryType = shipment.C?.toString().trim().toUpperCase() ?? ''
  shipment.C =
    typeShipment.find(
      (x) =>
        x.value.toString().toUpperCase() === rawDeliveryType ||
        x.name.toString().toUpperCase() === rawDeliveryType
    )?.value ??
    // Carga masiva-only: "Tienda" es el label visible de OFFICE en la plantilla; el
    // backend no lo conoce, así que lo mapeamos explícitamente. 'OFICINA' se mantiene
    // por compatibilidad con plantillas viejas.
    (rawDeliveryType === 'TIENDA' || rawDeliveryType === 'OFICINA'
      ? DeliveryEnum.OFFICE
      : '')

  shipment.D =
    citiesType.find(
      (x) =>
        x.locationCode.toString().toUpperCase() ===
          shipment.D?.toString().toUpperCase() ||
        x.locationName.toString().toUpperCase() ===
          shipment.D?.toString().toUpperCase()
    )?.locationCode ?? ''

  let rawStoreCode = shipment.E?.toString().trim()
  if (version === 'V2' && rawStoreCode)
    rawStoreCode = cleanStoreCode(rawStoreCode)

  // Inferir la tienda destino desde la ciudad (citiesByBu) cuando no viene explícita. En
  // V2 aplica tanto a Tienda (columna E vacía) como a DOMICILIO (no trae columna de tienda):
  // así la guía queda con "Tienda asignada" desde el import —la misma que el payload envía—
  // y no aparece vacía. Si hay coordenadas, la cobertura la refina luego en el Review.
  const shouldInferStore =
    version === 'V1' ||
    shipment.C === DeliveryEnum.OFFICE ||
    shipment.C === DeliveryEnum.DELIVERY
  const inferredStoreCode =
    shouldInferStore && (!rawStoreCode || rawStoreCode.length === 0)
      ? citiesByBuType.find(
          (x) =>
            x.locationCode ===
            citiesType.find(
              (c) =>
                c.locationCode.toString().toUpperCase() ===
                  shipment.D?.toString().toUpperCase() ||
                c.locationName.toString().toUpperCase() ===
                  shipment.D?.toString().toUpperCase()
            )?.locationCode
        )?.buCode
      : ''

  const finalStoreCodeToSearch = rawStoreCode || inferredStoreCode || ''
  let foundStore = businessUnits.find(
    (x) =>
      x.buCode.toString().toUpperCase() === finalStoreCodeToSearch.toUpperCase()
  )
  // Fallback: match against the full dropdown value "${buCode} - ${buName}" in case
  // cleanStoreCode didn't strip correctly (e.g., buCode contains internal hyphens).
  if (!foundStore && shipment.E) {
    const original = String(shipment.E).trim().toUpperCase()
    foundStore = businessUnits.find((x) => {
      const name =
        (x.buName?.toString().toUpperCase() ||
          x.buCode?.toString().toUpperCase()) ??
        ''
      const dropdownStr = `${x.buCode?.toString().toUpperCase()} - ${name}`
      return dropdownStr === original
    })
  }
  shipment.E = foundStore?.buCode ?? ''

  if (version === 'V2' && foundStore?.location.locationCode && !shipment.D) {
    shipment.D =
      foundStore.location.locationCode ??
      citiesType.find(
        (x) =>
          x.locationCode.toString().toUpperCase() ===
            shipment.D?.toString().toUpperCase() ||
          x.locationName.toString().toUpperCase() ===
            shipment.D?.toString().toUpperCase()
      )?.locationCode ??
      ''
  }

  if (version === 'V2' && shipment.C === DeliveryEnum.OFFICE && foundStore) {
    if (
      foundStore.location.address &&
      foundStore.location.address.trim().length > 0
    ) {
      shipment.N = foundStore.location.address
    }
  }

  shipment.F =
    identificationTypes
      .find(
        (x) =>
          (x.abbreviationName === user?.client?.abbreviationName &&
            (shipment.F === undefined ||
              shipment.F?.toString().trim().length === 0)) ||
          x.taxIdentificationTypeId?.toString() ===
            shipment.F?.toString().toUpperCase() ||
          x.abbreviationName?.slice(0, 1) ===
            shipment.F?.toString().toUpperCase() ||
          x.abbreviationName.toUpperCase() ===
            shipment.F?.toString().toUpperCase()
      )
      ?.taxIdentificationTypeId?.toString() ?? ''

  shipment.I =
    identificationTypes
      .find(
        (x) =>
          x.taxIdentificationTypeId.toString() ===
            shipment.I?.toString().toUpperCase() ||
          x.abbreviationName.slice(0, 1) ===
            shipment.I?.toString().toUpperCase() ||
          x.abbreviationName.toUpperCase() ===
            shipment.I?.toString().toUpperCase()
      )
      ?.taxIdentificationTypeId.toString() ?? ''

  shipment.G =
    shipment.G !== undefined &&
    shipment.G !== null &&
    shipment.G?.toString().trim().length > 0
      ? shipment.G?.toString().trim()
      : (user?.client?.identificationNumber?.toString().trim() ?? '')

  // Nombre Remitente vacío → usar el nombre del CLIENTE autorizado seleccionado
  // (listAuthorizingAccountClient[0].accountFullName), NO el accountFullName del sitio
  // (que es el accountSiteName). Mismo criterio que Inventory.tsx:281-283.
  shipment.H =
    shipment.H !== undefined &&
    shipment.H !== null &&
    shipment.H?.toString().trim().length > 0
      ? shipment.H?.toString().trim()
      : (user?.client?.listAuthorizingAccountClient?.[0]?.accountFullName
          ?.toString()
          .trim() ?? '')

  shipment.J = shipment.J?.toString().trim()
  shipment.K = shipment.K?.toString().trim()
  shipment.L = shipment.L?.toString().trim()
  shipment.M = shipment.M?.toString().trim()
  shipment.N = shipment.N?.toString().trim()
  shipment.O = shipment.O?.toString().trim()
  shipment.P = shipment.P?.toString().trim()
  shipment.Q = shipment.Q?.toString().trim()
  shipment.R = shipment.R?.toString()?.replace(',', '.').trim()
  shipment.S = shipment.S?.toString().trim()
  shipment.T = shipment.T?.toString().trim()
  shipment.U = shipment.U?.toString().trim()
  shipment.V = shipment.V?.toString().trim()

  if (version === 'V2') {
    const pName = shipment.W?.toString().trim()
    const pCode = shipment.X?.toString().trim()
    let catId = ''
    if (pCode) catId = pieceCategories.find((c) => c.code === pCode)?.id
    if (!catId && pName)
      catId = pieceCategories.find((c) => c.name === pName)?.id
    shipment.W = catId || '49'
  } else {
    shipment.W =
      pieceCategories.find((x) => x.id === shipment.W?.toString().trim())?.id ??
      ''
  }
}

function dataUrlToFile(
  dataUrl: string,
  name: string,
  type: string
): File | null {
  try {
    const [header, b64] = dataUrl.split(',')
    if (!b64) return null
    const mime = type || header.match(/:(.*?);/)?.[1] || ''
    const bin = atob(b64)
    const bytes = new Uint8Array(bin.length)
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
    return new File([bytes], name, { type: mime })
  } catch (_) {
    return null
  }
}

const ShipmentBulkUpload: FC = () => {
  const dispatch = useAppDispatch()
  const user = useAppSelector((state) => state.user)
  // Carga masiva: tiendas, ciudades, categorias y la relacion ciudad→tienda vienen del
  // sub-slice Django (fresco por sesion), no de inmutable (.NET). identificationTypes
  // sigue en .NET.
  const businessUnits = useAppSelector(
    (state) => state.bulkCatalogs.businessUnits
  )
  const identificationTypes = useAppSelector(
    (state) => state.inmutable.taxIdentificationTypes
  )
  // Catálogo .NET, sólo como respaldo para las tiendas del contrato que el de Django no
  // trae (las virtuales). Ver augmentCatalogWithContractStores.
  const netBusinessUnits = useAppSelector(
    (state) => state.inmutable.businessUnits
  )

  // Tiendas destino que el contrato permite (AgreementBU). MISMO hook y MISMA regla
  // que guía, preenvío, favoritos e import legacy: con al menos una fila
  // isSource=false se ofrecen SOLO esas; sin ninguna, el catálogo completo.
  // La base es el catálogo de Django (bulkCatalogs), sin buid ni buTypeID —el resolver
  // lo contempla—, completado abajo con las tiendas del contrato que ese catálogo no trae.
  const { rows: contractDestinationRows, status: contractStatus } =
    useContractDestinationBUs(user.client?.agreementID)

  // El catálogo de Django no trae las tiendas virtuales (buTypeID 140/160), así que un
  // contrato armado sobre ellas se quedaba sin destinos justo acá, mientras Crear guía
  // —que resuelve contra .NET— sí las ofrecía. Se completa el catálogo con las tiendas
  // del contrato que falten antes de resolver, para que las dos pantallas coincidan.
  const catalogForContract = useMemo(
    () =>
      augmentCatalogWithContractStores(
        businessUnits,
        netBusinessUnits,
        contractDestinationRows
      ),
    [businessUnits, netBusinessUnits, contractDestinationRows]
  )

  const citiesType = useAppSelector((state) => state.bulkCatalogs.cities)
  const citiesByBuType = useAppSelector(
    (state) => state.bulkCatalogs.citiesByBu
  )
  const pieceCategories = useAppSelector(
    (state) => state.bulkCatalogs.pieceCategories
  )
  const catalogsLoading = useAppSelector((state) => state.bulkCatalogs.loading)
  const catalogsError = useAppSelector((state) => state.bulkCatalogs.error)
  const catalogsLoaded = useAppSelector((state) => state.bulkCatalogs.loaded)
  const rangeDeclareList = useAppSelector(
    (state) => state.inmutable.rangeDeclareList
  )
  // Servicios que el operador puede elegir por guía. MISMA lista que arma el Review
  // (helper compartido): la plantilla no puede ofrecer un servicio que el Review luego
  // reemplace en silencio. El primero es además el default del mínimo valor declarado;
  // si aún no cargó, la exportación cae a los rangos genéricos (serviceID 0).
  const paymentModeRaw =
    user.paymentMethod?.[0]?.paymentModeID ?? PaymentModeId.CREDIT
  const { allowedServices, loaded: servicesLoaded } = useAllowedServices(
    paymentModeRaw as PaymentModeId
  )
  const serviceOptions = useMemo(
    () => authorizedServiceOptions(allowedServices, user.client, paymentModeRaw),
    [allowedServices, user.client, paymentModeRaw]
  )
  const defaultServiceId = serviceOptions?.[0]?.serviceID

  // Diagnóstico de la lista de servicios: imprime CADA etapa del filtrado para poder ver
  // en qué paso se pierden los servicios de un contrato (el hook trae N, el contrato
  // autoriza M, la intersección deja K). Ojo con los TIPOS: el cruce es
  // `authorizedServices.includes(s.serviceID)`, así que un serviceID string ("60") contra
  // uno numérico (60) no matchea y la lista sale vacía sin error visible.
  useEffect(() => {
    if (!servicesLoaded) return
    const listAuthorizing = (user.client?.listAuthorizingAccountClient ??
      []) as any[]
    const autorizadasDeLaModalidad = authorizingAccountsForMode(
      user.client,
      paymentModeRaw
    )
    /* eslint-disable no-console */
    console.groupCollapsed(
      `[carga-masiva] servicios → ${serviceOptions.length} elegible(s) · columna "Servicio" en el Excel: ${serviceOptions.length > 1 ? 'SÍ' : 'NO'}`
    )
    console.log('modalidad de pago (paymentModeRaw):', paymentModeRaw, `(${typeof paymentModeRaw})`)
    console.log('accountSiteID del usuario:', user.client?.accountSiteID, `(${typeof user.client?.accountSiteID})`)
    console.log('accountCode / cliente:', user.client?.accountCode, user.client?.id)

    console.log(`1) allowedServices (hook, tras modalidad+entrega+bloqueados): ${allowedServices.length}`)
    console.table(
      allowedServices.map((s) => ({
        serviceID: s.serviceID,
        tipo: typeof s.serviceID,
        serviceShortName: s.serviceShortName,
      }))
    )

    console.log(`2) listAuthorizingAccountClient completo: ${listAuthorizing.length}`)
    console.table(
      listAuthorizing.map((a) => ({
        serviceID: a.serviceID,
        tipoServiceID: typeof a.serviceID,
        accountSiteID: a.accountSiteID,
        paymentModeID: a.paymentModeID,
        tipoPaymentModeID: typeof a.paymentModeID,
        isValueDeclare: a.isValueDeclare,
      }))
    )

    console.log(`3) autorizadas que pasan el filtro de modalidad/sitio: ${autorizadasDeLaModalidad.length}`)
    console.table(
      autorizadasDeLaModalidad.map((a) => ({
        serviceID: a.serviceID,
        accountSiteID: a.accountSiteID,
        paymentModeID: a.paymentModeID,
      }))
    )

    console.log(`4) serviceOptions final (1 ∩ 3): ${serviceOptions.length}`)
    console.table(
      serviceOptions.map((s) => ({
        serviceID: s.serviceID,
        serviceShortName: s.serviceShortName,
      }))
    )

    if (allowedServices.length > 0 && autorizadasDeLaModalidad.length > 0 && serviceOptions.length === 0) {
      console.warn(
        'El hook trae servicios y el contrato autoriza servicios, pero la intersección quedó VACÍA. ' +
          'Compará los serviceID de las tablas 1 y 3 — si uno es string y el otro number, el cruce falla.'
      )
    }
    console.groupEnd()
    /* eslint-enable no-console */
  }, [
    servicesLoaded,
    allowedServices,
    serviceOptions,
    user.client,
    paymentModeRaw,
  ])

  // Hidrata los catalogos Django una vez por sesion al abrir carga masiva (A1: si
  // tiendas/ciudades fallan -> catalogsError, la UI se bloquea y avisa).
  useEffect(() => {
    dispatch(hydrateBulkCatalogs())
  }, [dispatch])

  // Carga masiva-only: OFFICE se muestra como "Tienda" (consistente con la plantilla
  // Excel). El valor interno sigue siendo DeliveryEnum.OFFICE.
  const typeShipment = [
    { value: DeliveryEnum.OFFICE, name: 'Tienda' },
    { value: DeliveryEnum.DELIVERY, name: 'Domicilio' },
  ]

  const [error, setError] = useState('')
  const [file, setFile] = useState<File>()
  const [groups, setGroups] = useState<ShipmentGroup[]>([])
  const [nextSave, setNextSave] = useState(false)
  const [loading, setLoading] = useState(false)
  const [loadingStatus, setLoadingStatus] = useState(
    ConfirmModalStatus.PROCESSING
  )
  const [loadingTitle, setLoadingTitle] = useState('Cargando...')
  const [initialValidationError, setInitialValidationError] = useState<
    string | null
  >(null)
  const [initialValidationDone, setInitialValidationDone] = useState(false)
  const [draftInfo, setDraftInfo] = useState<{
    fileName: string
    fileType?: string
    fileData?: string
    externalManifest?: string
    savedAt: number
    groups: ShipmentGroup[]
  } | null>(null)
  const [fileDataUrl, setFileDataUrl] = useState<string | null>(null)
  const [openManifestModal, setOpenManifestModal] = useState(false)
  const [externalManifest, setExternalManifest] = useState('')
  const [favoriteDestinations, setFavoriteDestinations] = useState<
    DestinationInterface[]
  >([])
  const [favoritesLoaded, setFavoritesLoaded] = useState(false)
  const [v2TemplateError, setV2TemplateError] = useState<{
    missing: string[]
  } | null>(null)
  // Aviso cuando el archivo subido venía en el formato del sistema anterior y se
  // convirtió al vigente. Se muestra durante toda la revisión: el operador tiene que
  // saber que ese formato no traía coordenadas/favoritos antes de enviar las guías.
  const [legacyNotice, setLegacyNotice] = useState<string | null>(null)
  // El fetch perezoso de oficinas origen se dispara una sola vez (la validación ahora
  // re-corre en cada cambio, así que sin este ref se repetiría en cada render).
  const originsFetchRequested = useRef(false)

  // Validación inicial (oficinas origen + servicios autorizados + cuenta para facturar).
  // Las tres son condiciones SIN las cuales la carga masiva no puede producir una guía
  // válida, así que se bloquea el flujo completo y se le dice al operador qué le falta
  // en vez de dejarlo llenar 200 filas para que el backend las rechace.
  useEffect(() => {
    if (!user) return

    // [D8] Sesión vieja sin la lista: si nunca se buscó (undefined) y hay
    // agreementID, hacer fetch perezoso y esperar a que el store se actualice
    // (evita false-block a un usuario multi-oficina antes de que hidrate).
    // El ref evita repetir el fetch si la respuesta no deja la lista definida.
    if (user.originBusinessUnits === undefined && !!user.client?.agreementID) {
      if (!originsFetchRequested.current) {
        originsFetchRequested.current = true
        getOriginBusinessUnits(user.client.agreementID).then((list) => {
          dispatch(setOriginBusinessUnits(list))
        })
      }
      return
    }

    // Mismo motivo que el early-return de arriba: los servicios se piden async y hasta
    // que las 3 fuentes del hook responden la lista está vacía por "todavía cargando",
    // no por "no tiene ninguno". Decidir antes sería un false-block.
    if (!servicesLoaded) return

    const missingData: string[] = []
    // [D3] Sin oficinas origen usables → bloquear carga masiva.
    if ((user.originBusinessUnits?.length ?? 0) === 0)
      missingData.push('oficinas origen disponibles')
    // Sin servicios autorizados no hay `billing.service` que enviar: toda guía fallaría.
    if (serviceOptions.length === 0)
      missingData.push('servicios asociados a su contrato')
    const paymentModeid =
      user.paymentMethod?.[0]?.paymentModeID ?? PaymentModeId.CREDIT
    const accountBillTo = user.paymentMethod?.filter(
      (x) => x.paymentModeID?.toString() === paymentModeid?.toString()
    )?.[0]?.accountBillTo
    if (
      (accountBillTo === '00000000-0000-0000-0000-000000000000' ||
        accountBillTo === undefined) &&
      paymentModeid.toString() === PaymentModeId.CREDIT.toString()
    ) {
      missingData.push('cuenta para facturar asignada')
    }
    // El bloqueo se RECALCULA en cada corrida, no se latchea: si la lista de servicios u
    // oficinas llega/cambia después, el aviso se limpia solo y los botones se rehabilitan.
    // El modal, en cambio, se abre una sola vez (no reaparece si el operador lo cerró).
    const msg = missingSetupMessage(missingData)
    setInitialValidationError(msg || null)
    if (msg && !initialValidationDone) {
      setLoadingStatus(ConfirmModalStatus.ERROR)
      setLoadingTitle(msg)
      setLoading(true)
    }
    setInitialValidationDone(true)
  }, [
    user,
    initialValidationDone,
    dispatch,
    servicesLoaded,
    serviceOptions.length,
  ])

  // Load favoriteDestinations once (needed for Excel template download + Review screen)
  useEffect(() => {
    const accountId = user?.client?.id
    if (!accountId || favoritesLoaded) return
    getFavoriteAndRecentDestinationsDjango(
      accountId,
      identificationTypes,
      catalogForContract,
      ''
    )
      .then((res) => {
        if (!res.didError && res.model) {
          setFavoriteDestinations(res.model.favoriteDestinations || [])
        }
      })
      .catch(() => {
        /* silent — template just won't have dropdown */
      })
      .finally(() => setFavoritesLoaded(true))
  }, [user?.client?.id, identificationTypes, catalogForContract, favoritesLoaded])

  // Load draft from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem('shipment-import-v2-draft')
      if (saved) {
        const draft = JSON.parse(saved)
        if (
          draft.version === 1 &&
          Array.isArray(draft.groups) &&
          draft.groups.length > 0
        ) {
          setDraftInfo(draft)
        }
      }
    } catch (_) {}
  }, [])

  useEffect(() => {
    if (!file) {
      setFileDataUrl(null)
      return
    }
    const reader = new FileReader()
    reader.onload = () =>
      setFileDataUrl(typeof reader.result === 'string' ? reader.result : null)
    reader.onerror = () => setFileDataUrl(null)
    reader.readAsDataURL(file)
  }, [file])

  // Auto-save draft (debounced 300ms)
  useEffect(() => {
    if (!nextSave || groups.length === 0) return
    const timer = setTimeout(() => {
      const base = {
        version: 1,
        savedAt: Date.now(),
        fileName: file?.name || '',
        externalManifest,
        groups,
      }
      try {
        localStorage.setItem(
          'shipment-import-v2-draft',
          JSON.stringify({
            ...base,
            fileType: file?.type || '',
            fileData: fileDataUrl || '',
          })
        )
      } catch (_) {
        try {
          localStorage.setItem('shipment-import-v2-draft', JSON.stringify(base))
        } catch (_) {}
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [groups, nextSave, file, fileDataUrl, externalManifest])

  const handleRestoreDraft = () => {
    if (!draftInfo) return
    setGroups(draftInfo.groups)
    if (draftInfo.fileData && draftInfo.fileName) {
      const restored = dataUrlToFile(
        draftInfo.fileData,
        draftInfo.fileName,
        draftInfo.fileType || ''
      )
      if (restored) setFile(restored)
    }
    setExternalManifest(draftInfo.externalManifest || '')
    setNextSave(true)
    setDraftInfo(null)
  }

  const handleDiscardDraft = () => {
    localStorage.removeItem('shipment-import-v2-draft')
    setDraftInfo(null)
  }

  const isCOD =
    user.paymentMethod?.some((m: any) => m.paymentModeID?.toString() === '2') ??
    false

  const { options: businessUnitOptions } = useMemo(
    () =>
      resolveDestinationBusinessUnits(
        catalogForContract,
        contractDestinationRows,
        new Date()
      ),
    [catalogForContract, contractDestinationRows]
  )

  // Mientras no sepamos qué permite el contrato no se genera la plantilla: el Excel
  // lleva la lista embebida como validación de datos y el cliente lo llena sin
  // conexión, así que uno bajado a medias reparte el catálogo completo.
  const templateBlocked = contractStatus !== 'ok'

  const handleFileChange = (f?: File) => {
    if (initialValidationError) {
      setError(initialValidationError)
      return
    }
    if (!f) {
      setFile(undefined)
      return
    }
    const name = f.name.toLowerCase()
    const isExcel =
      f.type ===
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      f.type === 'application/vnd.ms-excel' ||
      name.endsWith('.xlsx') ||
      name.endsWith('.xls')
    if (isExcel) {
      setFile(f)
      setError('')
    } else {
      setError('El archivo no es xls o xlsx')
    }
  }

  const handleFileSubmit = async () => {
    if (!file) {
      setError('Por favor seleccione un archivo')
      return
    }
    setError('')
    setLoading(true)
    setLoadingStatus(ConfirmModalStatus.PROCESSING)
    setLegacyNotice(null)
    // H.1: show the actual filename like V1 does, so operator knows which file
    // is being processed (defensive against confused click-twice flows).
    setLoadingTitle(`Procesando archivo: ${file.name}`)

    try {
      let result = await importShipmentsFromXLSX(file)

      // Build headerMap: header name (lowercase) → column letter
      const rawHeaderRow = result[0] as Record<string, any>
      result.shift()

      // `let` porque el adaptador del formato anterior lo REEMPLAZA más abajo (antes de
      // la primera lectura de celda); col/colRaw cierran sobre esta misma variable.
      let headerMap: Record<string, string> = {}
      Object.entries(rawHeaderRow).forEach(([letter, value]) => {
        if (value != null) {
          headerMap[String(value).trim().toLowerCase()] = letter
        }
      })

      // Read cell value by header name
      const col = (row: Record<string, any>, name: string): string => {
        const letter = headerMap[name.toLowerCase()]
        return letter ? String(row[letter] ?? '') : ''
      }

      // H.4: read raw cell value (pre-stringification) so extractCoord can handle
      // SheetJS Number values + Excel rich cell objects. Plain col() stringifies
      // to "[object Object]" which kills parseFloat. Helper colocated here so it
      // closes over headerMap; extractCoord itself lives in shipmentImportUtils.ts.
      const colRaw = (row: Record<string, any>, name: string): any => {
        const letter = headerMap[name.toLowerCase()]
        return letter ? row[letter] : undefined
      }

      // H.4: hoist debug flag check out of the per-row loop (was N reads for N rows).
      const debugImport =
        typeof window !== 'undefined' &&
        window.localStorage?.getItem('debug-import-v2') === '1'

      // V4: 27 cabeceras requeridas (Fecha eliminada; renombres + swap Ciudad↔Tienda).
      // Viven en utils/bulkTemplateHeaders.ts porque el adaptador del formato anterior y
      // sus tests necesitan LA MISMA lista (ver comentario de ese archivo).
      const REQUIRED_HEADERS = BULK_TEMPLATE_REQUIRED_HEADERS

      // Archivo generado por el SISTEMA ANTERIOR (hoja IntegrationCCWV2 / FormatoMasivo):
      // se traducen sus cabeceras a las de la plantilla vigente y el resto del parser
      // sigue igual. La detección exige la firma del formato viejo Y la ausencia de la
      // cabecera exclusiva de la plantilla actual, así que un archivo actual nunca entra
      // por acá. Ver utils/legacyBulkTemplate.ts.
      if (isLegacyBulkTemplate(headerMap)) {
        const adaptation = adaptLegacyBulkTemplate(headerMap, REQUIRED_HEADERS)
        if (adaptation.missingEssential.length > 0) {
          setLoadingStatus(ConfirmModalStatus.ERROR)
          setLoadingTitle(
            `El archivo parece del formato anterior, pero le faltan columnas necesarias: ${adaptation.missingEssential.join(', ')}. Descarga la plantilla vigente con el botón Descargar.`
          )
          return
        }
        headerMap = adaptation.headerMap
        // Columnas que el formato viejo no tiene y que el operador SÍ debe revisar. Las
        // de favorito y el nombre de categoría se omiten a propósito: el favorito es
        // opcional y la categoría ya queda resuelta por su código.
        const IRRELEVANT_WHEN_LEGACY = [
          '¿Es destinatario favorito? (Sí/No)',
          'Identificador destinatario favorito',
          'Categoria del contenido',
        ]
        const faltantes = adaptation.unmapped.filter(
          (h) => !IRRELEVANT_WHEN_LEGACY.includes(h)
        )
        setLegacyNotice(
          `Este archivo viene del formato anterior y se convirtió automáticamente a la plantilla vigente.` +
            (faltantes.length > 0
              ? ` Ese formato no incluye ${faltantes.join(', ')}: complétalas acá si hacen falta.`
              : '')
        )
      }

      const missing = REQUIRED_HEADERS.filter(
        (h) => !headerMap[h.toLowerCase()]
      )
      // Check if this is a V2-era template (missing only the two new favorite headers)
      const onlyFavMissing =
        missing.length === 2 &&
        missing.includes('¿Es destinatario favorito? (Sí/No)') &&
        missing.includes('Identificador destinatario favorito')
      if (missing.length > 0) {
        if (onlyFavMissing) {
          // Friendly V2 → V3 migration modal
          setLoading(false)
          setV2TemplateError({ missing })
          return
        }
        setLoadingStatus(ConfirmModalStatus.ERROR)
        setLoadingTitle(
          `Formato incorrecto — headers faltantes: ${missing.slice(0, 3).join(', ')}${missing.length > 3 ? '...' : ''}. Por favor descarga el nuevo template con el botón Descargar.`
        )
        return
      }

      // Filter empty rows (solo basado en el número de guía; Fecha ya no existe)
      result = result.filter((row) => {
        return col(row, 'Número de guía aliada').trim().length > 0
      })

      if (result.length === 0) {
        setLoadingStatus(ConfirmModalStatus.ERROR)
        setLoadingTitle(
          'El archivo no contiene guías válidas. Verifique que la columna "Número de guía aliada" tenga valores.'
        )
        return
      }

      // Ciudad llega como "Localidad - Código"; extraemos el código (último segmento
      // tras " - "). Si no trae sufijo, se pasa tal cual (normalize matchea por nombre).
      const cityCodeFromCell = (rawCity: string): string => {
        const s = (rawCity || '').trim()
        if (!s) return ''
        const i = s.lastIndexOf(' - ')
        return i >= 0 ? s.slice(i + 3).trim() : s
      }

      // Build semantic objects for normalizeShipmentByVersion.
      // Las LETRAS del ImportInterface son el contrato interno de normalize (no las letras
      // de la plantilla); solo cambian los NOMBRES de header que se leen con col().
      const toSemantic = (raw: Record<string, any>): ImportInterface =>
        ({
          A: '',
          B: col(raw, 'Número de guía aliada'),
          C: col(raw, 'Tipo de entrega'),
          D: cityCodeFromCell(
            col(raw, 'Ciudad/Localidad destino (si es domicilio)')
          ),
          E: col(raw, 'Código de tienda destino (si es entrega en tienda)'),
          F: col(raw, 'Tipo de documento Remitente (Opcional)'),
          G: col(raw, 'Nº de Documento Remitente (Opcional)'),
          H: col(raw, 'Nombre Remitente (Opcional)'),
          I: col(raw, 'Tipo de documento destinatario'),
          J: col(raw, 'Nº de Documento destinatario'),
          K: col(raw, 'Nombre destinatario'),
          L: col(raw, 'Telefono destinatario'),
          M: col(raw, 'Correo destinatario'),
          N: col(raw, 'Dirección destino (si es domicilio)'),
          O: col(raw, 'Codigo Postal (Opcional)'),
          P: col(raw, 'Observaciones'),
        }) as ImportInterface

      const semanticRows = result.map((raw) => toSemantic(raw))
      semanticRows.forEach((shipment) => {
        normalizeShipmentByVersion(shipment, 'V2', {
          typeShipment,
          citiesType,
          citiesByBuType,
          businessUnits: catalogForContract,
          identificationTypes,
          pieceCategories,
          user,
        })
      })

      // Check duplicates in DB — POST /api/shipment/existing (Django). Compara (case-insensitive)
      // contra ShipmentNumber y ShipmentHeaderReference; devuelve solo las que existen. Enviamos
      // la lista única (el backend recorta/descarta blancos/dedupe, pero deduplicamos igual para
      // aligerar el payload) en lotes de 5000 (límite del endpoint). account_code /
      // shipper_account_id acotan la referencia aliada por cliente; se omiten cuando están vacíos.
      setLoadingTitle('Verificando guías existentes...')
      const trackingValues = result.map((r) =>
        col(r, 'Número de guía aliada').trim().toUpperCase()
      )
      const uniqueTracking = Array.from(new Set(trackingValues.filter(Boolean)))
      const accountSiteCode = user?.client?.accountCode ?? ''
      const accountId = user?.client?.id ?? ''
      const accountScope: {
        account_code?: string
        shipper_account_id?: string
      } = accountSiteCode
        ? { account_code: accountSiteCode }
        : accountId
          ? { shipper_account_id: accountId }
          : {}
      const existingSet = new Set<string>()
      let existingCheckFailed = ''
      const EXISTING_CHUNK = 5000
      for (let i = 0; i < uniqueTracking.length; i += EXISTING_CHUNK) {
        const chunk = uniqueTracking.slice(i, i + EXISTING_CHUNK)
        const res = await getShipmentsExisting({
          shipment_identifiers: chunk,
          ...accountScope,
        })
        // En error el envelope trae model:null → no marcamos duplicados (no bloqueamos la carga),
        // pero SÍ lo avisamos: desde el cambio de API un account_code inválido responde
        // `account.AccountSite.1` en vez de lista vacía, y sin aviso el operador leería
        // "ninguna guía duplicada" cuando en realidad no se verificó nada.
        if (res.didError && !existingCheckFailed) {
          existingCheckFailed =
            res.code === 'account.AccountSite.1'
              ? `El código de cliente (${accountSiteCode}) no es válido para esta cuenta, así que no se pudo verificar si las guías ya existen.`
              : res.errorMessage ||
                'No se pudo verificar si las guías ya existen.'
        }
        res.model?.existing_shipment_identifiers?.forEach((s) =>
          existingSet.add(String(s).trim().toUpperCase())
        )
      }
      if (existingCheckFailed) {
        alertService.warn(
          'No se verificaron guías duplicadas',
          `${existingCheckFailed} Revisa los números antes de enviar.`,
          { autoClose: false }
        )
      }
      // COD: pre-resolve shipper doc type from user account
      const userShipperDocTypeId =
        identificationTypes
          .find((t) => t.abbreviationName === user.client?.abbreviationName)
          ?.taxIdentificationTypeId?.toString() ?? ''

      // Category derivation: code or name → category id (string/number safe)
      const deriveCategoryId = (name: string, code: string): string => {
        const trimmedCode = code?.trim()
        const trimmedName = name?.trim()
        const byCode = pieceCategories.find(
          (c) =>
            String(c.code || '').trim() === trimmedCode ||
            String(c.id || '').trim() === trimmedCode
        )
        if (byCode) return String(byCode.id)
        const byName = pieceCategories.find(
          (c) =>
            String(c.name || '')
              .trim()
              .toLowerCase() === trimmedName?.toLowerCase()
        )
        if (byName) return String(byName.id)
        // 5A: si el usuario escribió categoría/código pero no resolvió contra la lista,
        // devolver '' para que validatePiece marque la fila como error (en vez de
        // enmascararlo con la categoría por defecto). Solo cae al default si vino vacío.
        if (trimmedCode || trimmedName) return ''
        return '49'
      }

      // Piece deduplication key: productCode + weight + height + width + length
      const pieceKey = (raw: Record<string, any>): string =>
        [
          col(raw, 'Código de categoría'),
          col(raw, 'Peso en Kg por pieza'),
          col(raw, 'Dimension Alto por pieza'),
          col(raw, 'Dimension Ancho por pieza'),
          col(raw, 'Dimension Largo por pieza'),
        ]
          .map((v) => String(v || '').trim())
          .join('|')

      // Group by tracking number — identical pieces are merged (sum quantity)
      const groupMap = new Map<string, ShipmentGroup>()
      const pieceMaps = new Map<string, Map<string, ShipmentPieceRow>>()

      // [F1] La celda "Oficina origen" del dropdown trae "buCode - nombre"; tomamos
      // el buCode (prefijo antes de " - ") y NO confiamos en el texto: si matchea el
      // catálogo devolvemos su buCode, si no, preservamos el token para que la
      // validación del Review lo marque como inválido. Vacía → undefined (auto-asignar).
      const originOffices = user.originBusinessUnits ?? []
      const parseOriginBuCode = (cell: string): string | undefined => {
        const rawCell = (cell || '').trim()
        if (!rawCell) return undefined
        const token = rawCell.split(' - ')[0].trim()
        const match = originOffices.find((b) => b.buCode === token)
        return match ? match.buCode : token || undefined
      }

      // Columna "Servicio": mismo criterio condicional que el origen. Vacía o ausente
      // (plantilla de contrato con 1 solo servicio, o template viejo) → undefined y el
      // Review asigna el único/primer servicio, como venía siendo. Con texto que NO
      // resuelve contra los servicios autorizados devolvemos null: el servicio se deja sin
      // asignar y la fila se marca con un aviso visible, en vez de sustituirlo en silencio.
      const parseServiceCell = (cell: string) =>
        resolveServiceIdFromCell(cell, serviceOptions)

      for (let idx = 0; idx < result.length; idx++) {
        const raw = result[idx]
        const sem = semanticRows[idx]

        const tracking = col(raw, 'Número de guía aliada').trim().toUpperCase()
        if (!tracking) continue

        if (!groupMap.has(tracking)) {
          // H.4: read raw cell for coord columns so the defensive extractor can handle
          // SheetJS number values, comma-decimal strings, and rich cell objects uniformly.
          const rawLat = colRaw(raw, 'Latitud destino (si es domicilio)')
          const rawLng = colRaw(raw, 'Longitud destino (si es domicilio)')

          // V3: check if Excel row marks this as favorite + has valid identifier
          const esFavoritoRaw = col(raw, '¿Es destinatario favorito? (Sí/No)')
            .trim()
            .toLowerCase()
          const esFavorito =
            esFavoritoRaw === 'sí' ||
            esFavoritoRaw === 'si' ||
            esFavoritoRaw === 's' ||
            esFavoritoRaw === 'yes' ||
            esFavoritoRaw === 'y'
          // H.2: explicit "No" means operator opted out — don't nag with RIF auto-suggest
          // later. Blank/empty is different (operator didn't decide).
          const optedOutOfFavorite =
            esFavoritoRaw === 'no' || esFavoritoRaw === 'n'
          const favIdentifierRaw = col(
            raw,
            'Identificador destinatario favorito'
          ).trim()
          // V3+E: column G now holds account.displayName (from the hidden Favoritos sheet).
          // Match by displayName first; fall back to legacy RIF parser for backward compat.
          let matchedFav: (typeof favoriteDestinations)[number] | undefined
          if (esFavorito && favIdentifierRaw) {
            matchedFav = favoriteDestinations.find(
              (f) => (f.account?.displayName || '').trim() === favIdentifierRaw
            )
            if (!matchedFav) {
              const favRif = parseFavoriteIdentifier(favIdentifierRaw)
              if (favRif) {
                matchedFav = favoriteDestinations.find(
                  (f) =>
                    (f.account?.identificationNumber || '').trim() === favRif
                )
              }
            }
          }

          // Build base group from Excel fields (will be overridden below if fav match)
          // V3+: lat/lng only apply to home delivery. Force null when OFFICE.
          const isOfficeDelivery = sem.C === DeliveryEnum.OFFICE
          // H.4: extractCoord handles Number, string-with-comma, rich cell object.
          // Replaces the old safeParseCoord which only handled strings.
          const parsedLat = extractCoord(rawLat)
          const parsedLng = extractCoord(rawLng)
          if (debugImport) {
            // eslint-disable-next-line no-console
            console.log('[import-v2] coord parse', {
              tracking,
              rawLat,
              rawLng,
              rawLatType: typeof rawLat,
              rawLngType: typeof rawLng,
              parsedLat,
              parsedLng,
              deliveryType: sem.C,
              isOfficeDelivery,
            })
          }
          const baseGroup: ShipmentGroup = {
            // crypto.randomUUID requires secure context (HTTPS). uuid v4 works everywhere.
            id: uuidv4(),
            shipmentHeaderReference: tracking,
            deliveryType: sem.C,
            destinationCity: sem.D,
            storeCode: sem.E,
            // [ENG-1] Origen POR GUÍA: se toma de la primera fila del tracking (este
            // baseGroup solo se construye en la primera fila). Backward compat: si el
            // template no trae la columna (contrato de 1 sola oficina), col() → '' →
            // undefined y buildShipmentList auto-asigna la única oficina.
            originStoreCode: parseOriginBuCode(col(raw, 'Oficina origen')),
            // Servicio POR GUÍA: igual que el origen, se toma de la primera fila del
            // tracking. `null` (texto que no resolvió) se normaliza a undefined y el
            // aviso de la fila se agrega más abajo.
            serviceId: parseServiceCell(col(raw, 'Servicio')) ?? undefined,
            shipperDocType: isCOD ? userShipperDocTypeId : sem.F,
            shipperDocNumber: isCOD
              ? (user.client?.identificationNumber ?? '')
              : sem.G,
            shipperName: isCOD
              ? (user.client?.listAuthorizingAccountClient?.[0]
                  ?.accountFullName ?? '')
              : sem.H,
            consigneeDocType: sem.I,
            consigneeDocNumber: sem.J,
            consigneeName: sem.K,
            consigneePhone: normalizePhone(sem.L),
            consigneeEmail: sem.M,
            destinationAddress: sem.N,
            postalCode: sem.O,
            notes: sem.P,
            latitude: isOfficeDelivery ? null : parsedLat,
            longitude: isOfficeDelivery ? null : parsedLng,
            // G.5: punto de referencia (lee directo del raw para mantener toSemantic estable).
            // Backward compat: si el template viejo no tiene esta columna, col() retorna ''.
            addressReference: isOfficeDelivery
              ? ''
              : col(raw, 'Punto de referencia (si es domicilio)'),
            pieces: [],
            isShipperLocked: isCOD,
            isDuplicateInDB: existingSet.has(tracking),
            // H.2: persist explicit F=No so RIF auto-suggest banner doesn't fire later.
            optedOutOfFavorite: optedOutOfFavorite || undefined,
          }

          // V3: if Excel marked favorite + matched, override consignee fields from the favorite
          if (matchedFav) {
            const acc = matchedFav.account
            const loc = matchedFav.location
            const bu = matchedFav.businessUnit
            baseGroup.consigneeDocType =
              acc?.taxIdentificationTypeID == null
                ? ''
                : String(acc.taxIdentificationTypeID)
            baseGroup.consigneeDocNumber = acc?.identificationNumber || ''
            baseGroup.consigneeName = acc?.accountFullName || ''
            baseGroup.consigneePhone = normalizePhone(
              acc?.listAccountPhone?.[0]?.phoneNumber || ''
            )
            baseGroup.consigneeEmail = acc?.listAccountEmail?.[0]?.email || ''
            baseGroup.destinationAddress =
              loc?.address || baseGroup.destinationAddress
            baseGroup.postalCode = loc?.postalCode || baseGroup.postalCode
            baseGroup.storeCode = bu?.buCode || baseGroup.storeCode
            baseGroup.destinationCity =
              bu?.location?.locationCode || baseGroup.destinationCity

            // G.4-bis (CEO Review): detect silent override of Excel-supplied deliveryType.
            // If user wrote 'Domicilio' in M but the favorite is office (or vice versa),
            // the favorite was winning silently — violates "no silent failures" rule.
            // Now we surface the override as a visible warning attached to the group.
            const favDeliveryType = matchedFav.isHomeDelivery
              ? DeliveryEnum.DELIVERY
              : DeliveryEnum.OFFICE
            const excelDeliveryType = baseGroup.deliveryType
            if (excelDeliveryType && excelDeliveryType !== favDeliveryType) {
              const excelLabel =
                excelDeliveryType === DeliveryEnum.DELIVERY
                  ? 'Domicilio'
                  : 'Tienda'
              const favLabel = matchedFav.isHomeDelivery
                ? 'Domicilio'
                : 'Tienda'
              baseGroup.importWarning =
                `Tipo de entrega del Excel (${excelLabel}) reemplazado por el del favorito (${favLabel}). ` +
                `Si quieres entrega ${excelLabel}, cambia "¿Es destinatario favorito?" a No o elige otro favorito.`
            }
            baseGroup.deliveryType = favDeliveryType
            // V3+: lat/lng only apply to home delivery favorites. Office favorites stay coordinate-free.
            if (matchedFav.isHomeDelivery) {
              baseGroup.latitude = loc?.coordinates?.lat ?? baseGroup.latitude
              baseGroup.longitude = loc?.coordinates?.lng ?? baseGroup.longitude
            } else {
              baseGroup.latitude = null
              baseGroup.longitude = null
            }
            baseGroup.favoriteDestinationId = matchedFav.id || acc?.id || ''
          }

          // Servicio escrito en el Excel que no está entre los autorizados: no se
          // sustituye en silencio. Se avisa en la fila y el Review deja el dropdown
          // en el servicio por defecto para que el operador lo corrija a la vista.
          const rawServiceCell = col(raw, 'Servicio').trim()
          if (rawServiceCell && parseServiceCell(rawServiceCell) === null) {
            const serviceWarning =
              `El servicio "${rawServiceCell}" no está autorizado para tu contrato. ` +
              `Se dejó el servicio por defecto — verifícalo antes de enviar` +
              (serviceOptions.length > 0
                ? ` (disponibles: ${serviceOptions.map(serviceLabel).join(', ')}).`
                : '.')
            baseGroup.importWarning = baseGroup.importWarning
              ? `${baseGroup.importWarning} ${serviceWarning}`
              : serviceWarning
          }

          groupMap.set(tracking, baseGroup)
          pieceMaps.set(tracking, new Map<string, ShipmentPieceRow>())
        }

        const pMap = pieceMaps.get(tracking)!
        const pKey = pieceKey(raw)
        const qty = parseInt(col(raw, 'Cantidad de Piezas')) || 1

        if (pMap.has(pKey)) {
          const ep = pMap.get(pKey)!
          ep.quantity += qty
          ep.mergedRowCount = (ep.mergedRowCount || 1) + 1
        } else {
          pMap.set(pKey, {
            quantity: qty,
            weight:
              parseFloat(col(raw, 'Peso en Kg por pieza').replace(',', '.')) ||
              0,
            height:
              parseFloat(
                col(raw, 'Dimension Alto por pieza').replace(',', '.')
              ) || 0,
            width:
              parseFloat(
                col(raw, 'Dimension Ancho por pieza').replace(',', '.')
              ) || 0,
            length:
              parseFloat(
                col(raw, 'Dimension Largo por pieza').replace(',', '.')
              ) || 0,
            declaredValue:
              parseFloat(
                col(raw, 'Valor declarado por pieza').replace(',', '.')
              ) || 0,
            productName: col(raw, 'Categoria del contenido'),
            productCode: col(raw, 'Código de categoría'),
            categoryId: deriveCategoryId(
              col(raw, 'Categoria del contenido'),
              col(raw, 'Código de categoría')
            ),
            mergedRowCount: 1,
          })
        }
      }

      // Attach pieces to groups
      const grouped = Array.from(groupMap.values()).map((g) => ({
        ...g,
        pieces: Array.from(
          pieceMaps.get(g.shipmentHeaderReference) ||
            new Map<string, ShipmentPieceRow>()
        ).map(([, p]) => p),
      }))

      setGroups(grouped)
      setNextSave(true)
      setLoading(false)
    } catch (err: any) {
      console.error(err)
      setLoadingStatus(ConfirmModalStatus.ERROR)
      setLoadingTitle(err?.message || 'Error procesando el archivo')
    }
  }

  useEffect(() => {
    if (!nextSave) {
      setError('')
      setFile(undefined)
      setGroups([])
      setLegacyNotice(null)
    }
  }, [nextSave])

  return (
    <MainPage>
      <PageTitle title="Cargar guías" />

      {draftInfo && !nextSave && (
        <div className="w-full max-w-xl mx-auto mb-4 bg-amber-50 border border-amber-200 rounded-lg p-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <span className="text-sm text-amber-800">
            Tienes un proceso en curso (
            {draftInfo.fileName || 'archivo desconocido'},{' '}
            {new Date(draftInfo.savedAt).toLocaleDateString('es-VE')}). ¿Quieres
            retomarlo?
          </span>
          <div className="flex gap-2 flex-shrink-0">
            <PrimaryButton
              onClick={handleRestoreDraft}
              className="h-8 text-xs px-3"
            >
              Retomarlo
            </PrimaryButton>
            <button
              type="button"
              onClick={handleDiscardDraft}
              className="h-8 text-xs text-gray-500 hover:text-gray-700 px-2"
            >
              Descartar
            </button>
          </div>
        </div>
      )}

      {!nextSave && (
        <div className="flex flex-1 flex-col items-center justify-center mt-6 gap-4 max-w-xl mx-auto w-full">
          {/* El modal de error se puede cerrar; sin este aviso el operador se quedaba
              con los botones deshabilitados y sin explicación a la vista. */}
          {initialValidationError && (
            <div
              className="w-full rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 flex items-center justify-between gap-3"
              role="alert"
            >
              <span>{initialValidationError}</span>
              <SecondaryButton
                className="h-8 text-xs px-3 flex-shrink-0"
                onClick={() => {
                  setLoadingStatus(ConfirmModalStatus.ERROR)
                  setLoadingTitle(initialValidationError)
                  setLoading(true)
                }}
              >
                Ver detalle
              </SecondaryButton>
            </div>
          )}
          {catalogsError && (
            <div className="w-full rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 flex items-center justify-between gap-3">
              <span>
                No se pudieron cargar los catálogos (tiendas/ciudades) desde el
                servidor. No es posible descargar la plantilla ni cargar guías
                hasta reintentar.
              </span>
              <SecondaryButton
                className="h-8 text-xs px-3 flex-shrink-0"
                onClick={() => dispatch(hydrateBulkCatalogs(true))}
              >
                Reintentar
              </SecondaryButton>
            </div>
          )}
          <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="flex flex-col items-center gap-3 p-4 rounded-lg border border-gray-200 bg-white text-center">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-50 text-blue-600 font-bold text-sm">
                1
              </span>
              <p className="font-semibold text-sm text-gray-800">
                Descarga el formato
              </p>
              <PrimaryButton
                onClick={() => {
                  // La guarda va TAMBIÉN acá: el `disabled` de PrimaryButton es
                  // cosmético (se desestructura y nunca llega al <button>), así que
                  // frena el mouse pero no el teclado.
                  if (templateBlocked) return
                  const hasCredit = !!user.paymentMethod?.find(
                    (x: any) => x.paymentModeID.toString() === '3'
                  )
                  exportFormatoShipmentXLSX_V2WithCoords(
                    identificationTypes,
                    typeShipment,
                    businessUnitOptions,
                    citiesType,
                    pieceCategories,
                    !hasCredit,
                    favoriteDestinations,
                    rangeDeclareList,
                    defaultServiceId,
                    user.originBusinessUnits ?? [],
                    serviceOptions
                  )
                }}
                disabled={
                  !favoritesLoaded ||
                  !catalogsLoaded ||
                  catalogsError ||
                  // Sin saber qué tiendas permite el contrato, la plantilla saldría
                  // con el catálogo completo embebido como validación de datos.
                  templateBlocked ||
                  // Sin la lista real de servicios la plantilla saldría SIN la columna
                  // "Servicio" aunque el contrato tenga varios, y todas las guías se
                  // crearían con el servicio por defecto sin que el operador lo note.
                  !servicesLoaded ||
                  // Faltan oficinas origen / servicios / cuenta: no tiene sentido bajar
                  // una plantilla que después no va a poder cargar.
                  !!initialValidationError
                }
                className="w-full h-11 text-sm"
              >
                <DocumentArrowDownIcon className="w-4 h-4 mr-1" />
                {catalogsLoading
                  ? 'Cargando catálogos…'
                  : !servicesLoaded
                    ? 'Cargando servicios…'
                    : !favoritesLoaded
                      ? 'Cargando favoritos…'
                      : templateBlocked
                        ? 'Cargando tiendas de tu contrato…'
                        : 'Descargar'}
              </PrimaryButton>
            </div>

            <div className="flex flex-col items-center gap-3 p-4 rounded-lg border border-gray-200 bg-white text-center">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-500 font-bold text-sm">
                2
              </span>
              <p className="font-semibold text-sm text-gray-800">Complétalo</p>
              <p className="text-xs text-gray-500">
                Llena los datos de tus guías en el archivo
              </p>
            </div>
          </div>

          <div className="w-full rounded-lg border border-gray-200 bg-white p-4">
            <div className="flex flex-col items-center gap-3 mb-3 text-center">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-500 font-bold text-sm">
                3
              </span>
              <p className="font-semibold text-sm text-gray-800">Súbelo aquí</p>
            </div>
            <FormFile
              selected={file}
              error={error}
              label="Selecciona tu archivo"
              description="Archivos admitidos: XLS | XLSX"
              accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              id="shipment-import-v2-file"
              name="shipment-import-v2-file"
              onSelectFile={handleFileChange}
            />
            <div className="mt-3">
              <PrimaryButton
                onClick={() => {
                  if (!file) {
                    setError('Por favor seleccione un archivo')
                    return
                  }
                  setError('')
                  setOpenManifestModal(true)
                }}
                className="w-full h-11"
                disabled={
                  !file ||
                  !favoritesLoaded ||
                  !catalogsLoaded ||
                  catalogsError ||
                  // Sin la lista de servicios el parser no puede resolver la columna
                  // "Servicio" y marcaría como no autorizada cualquier fila que la traiga.
                  !servicesLoaded ||
                  // Sin oficinas origen / servicios / cuenta para facturar no se puede
                  // construir una guía válida: se bloquea la carga y se explica arriba.
                  !!initialValidationError
                }
              >
                {!servicesLoaded
                  ? 'Cargando servicios…'
                  : favoritesLoaded
                    ? 'Cargar archivo'
                    : 'Cargando favoritos…'}
              </PrimaryButton>
              {!favoritesLoaded && file && (
                <p className="mt-2 text-xs text-amber-700">
                  Esperando favoritos antes de importar — evita que las filas
                  con "Es destinatario favorito = Sí" se importen sin datos.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {nextSave && (
        <>
          <div className="flex flex-1 justify-between gap-12 md:gap-12 flex-col-reverse md:flex-row mt-2">
            <LinkTextIcon
              className="text-sm font-medium cursor-pointer"
              onClick={() => setNextSave(false)}
            >
              <ArrowLeftIcon className="h-4 w-4 mr-1" aria-hidden="true" />
              <label>Regresar</label>
            </LinkTextIcon>
          </div>
          {legacyNotice && (
            <div
              className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 flex items-start justify-between gap-3"
              role="status"
            >
              <span>{legacyNotice}</span>
              <button
                type="button"
                onClick={() => setLegacyNotice(null)}
                className="text-xs text-amber-700 hover:text-amber-900 flex-shrink-0"
              >
                Entendido
              </button>
            </div>
          )}
          <ShipmentBulkUploadReview
            groups={groups}
            setGroups={setGroups}
            setNext={setNextSave}
            isCOD={isCOD}
            businessUnits={catalogForContract}
            identificationTypes={identificationTypes}
            citiesType={citiesType}
            citiesByBu={citiesByBuType}
            pieceCategories={pieceCategories}
            favoriteDestinations={favoriteDestinations}
            file={file}
            externalManifest={externalManifest}
          />
        </>
      )}

      <Modal
        open={openManifestModal}
        setOpen={setOpenManifestModal}
        className="w-full max-w-lg"
      >
        <div className="flex flex-col gap-4">
          <h2 className="text-lg font-medium text-gray-900">
            Ingresar Nº Manifiesto Externo
          </h2>
          <p className="text-sm text-gray-500">
            Esta acción es opcional, si desea puede ingresar el número de
            manifiesto externo / número externo
          </p>
          <FormText
            id="external-manifest"
            name="external-manifest"
            label="Nº Manifiesto Externo / Nº identificación externo"
            value={externalManifest}
            onChange={(e) => setExternalManifest(e.target.value)}
            containerClassname="w-full"
            labelContainerClassname="mb-0"
          />
          <div className="flex justify-between gap-2 mt-2">
            <SecondaryButton
              onClick={() => setOpenManifestModal(false)}
              className="w-24"
            >
              Cancelar
            </SecondaryButton>
            <PrimaryButton
              onClick={() => {
                setOpenManifestModal(false)
                handleFileSubmit()
              }}
              className="w-24"
            >
              Siguiente
            </PrimaryButton>
          </div>
        </div>
      </Modal>

      <ModalLoading
        open={loading}
        title={loadingTitle}
        status={loadingStatus}
        setOpen={setLoading}
        onPrint={() => {}}
      />

      {v2TemplateError && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="v2-template-error-title"
        >
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-4 py-3 border-b border-gray-200">
              <h2
                id="v2-template-error-title"
                className="text-red-600 text-base font-semibold"
              >
                Template antiguo detectado
              </h2>
            </div>
            <div className="p-4 text-sm text-gray-700 space-y-2">
              <p>
                Tu archivo usa una versión antigua del template que no incluye
                las columnas de <strong>destinatario favorito</strong>.
              </p>
              <p className="text-xs text-gray-500">
                Faltan: {v2TemplateError.missing.join(', ')}
              </p>
              <p>Descarga el nuevo template y vuelve a cargar tus datos.</p>
            </div>
            <div className="flex justify-end gap-3 px-4 py-3 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setV2TemplateError(null)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 rounded-md border border-gray-200 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <PrimaryButton
                onClick={() => {
                  const hasCredit = !!user.paymentMethod?.find(
                    (x: any) => x.paymentModeID.toString() === '3'
                  )
                  exportFormatoShipmentXLSX_V2WithCoords(
                    identificationTypes,
                    typeShipment,
                    businessUnitOptions,
                    citiesType,
                    pieceCategories,
                    !hasCredit,
                    favoriteDestinations,
                    rangeDeclareList,
                    defaultServiceId,
                    user.originBusinessUnits ?? [],
                    serviceOptions
                  )
                  setV2TemplateError(null)
                }}
                disabled={!favoritesLoaded}
                className="text-sm"
              >
                Descargar nuevo template
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </MainPage>
  )
}

export default ShipmentBulkUpload
